/**
 * ChatSidebar — 聊天侧边栏组件
 *
 * 功能：
 * - 显示会话列表
 * - 新建对话按钮
 * - 删除会话按钮
 * - 当前活跃会话高亮
 * - 移动端遮罩层
 */

import React from 'react';
import { NewChatIcon, TrashIcon } from '../Icons';
import type { ChatSessionRecord } from '@/service/chat';
import './index.scss';

interface ChatSidebarProps {
  /** 会话列表 */
  sessions: ChatSessionRecord[];
  /** 当前活跃会话 ID */
  activeSessionId: number | null;
  /** 侧边栏是否展开（移动端） */
  open: boolean;
  /** 加载中 */
  loading: boolean;
  /** 新建对话回调 */
  onNewChat: () => void;
  /** 切换会话回调 */
  onSelectSession: (sessionId: number) => void;
  /** 删除会话回调 */
  onDeleteSession: (sessionId: number) => void;
  /** 关闭侧边栏回调 */
  onClose: () => void;
}

/**
 * 聊天侧边栏组件
 */
const ChatSidebar: React.FC<ChatSidebarProps> = ({
  sessions,
  activeSessionId,
  open,
  loading,
  onNewChat,
  onSelectSession,
  onDeleteSession,
  onClose,
}) => {
  return (
    <>
      {/* 侧边栏主体 */}
      <aside className={`chat-sidebar ${open ? 'open' : ''}`}>
        {/* 顶部：新建对话按钮 */}
        <div className="chat-sidebar-header">
          <button className="chat-new-btn" onClick={onNewChat} disabled={loading}>
            <NewChatIcon />
            新对话
          </button>
        </div>

        {/* 会话列表 */}
        <div className="chat-session-list">
          {sessions.map((session) => (
            <div
              key={session.id}
              className={`chat-session-item ${session.id === activeSessionId ? 'active' : ''}`}
              onClick={() => {
                onSelectSession(session.id);
                onClose();
              }}
            >
              {/* 会话标题 */}
              <span className="chat-session-title" title={session.title}>
                {session.title}
              </span>
              {/* 删除按钮 */}
              <button
                className="chat-session-delete"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteSession(session.id);
                }}
                title="删除会话"
                aria-label={`删除会话 ${session.title}`}
              >
                <TrashIcon />
              </button>
            </div>
          ))}
          {/* 空状态 */}
          {sessions.length === 0 && (
            <div className="chat-session-empty">暂无历史对话</div>
          )}
        </div>
      </aside>

      {/* 移动端遮罩 */}
      {open && <div className="chat-sidebar-overlay" onClick={onClose} />}
    </>
  );
};

export default ChatSidebar;
